import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useStreamingResponseMutation } from '../../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	updateEmailExecAssistantTemplatesRQCache,
	useEmailExecAssistantTemplatesSuspenseQuery,
} from '../../../../../queries/EmailExecAssistant/useEmailExecTemplates';
import { Button } from '../../../../components-v2/Button';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxValue,
} from '../../../../components-v2/Combobox';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { MAX_IGNORE_NOISE_NOTES_LENGTH, MAX_KEYWORDS } from './constants';
import { styleSheet } from './styles';
import { addKeywords, splitKeywordInput } from './utils';
import { InputGroupAddon, InputGroupText, InputGroupTextarea, InputGroupV2 } from '@HoudiniUI/InputGroup';
import { PlayIcon } from '@WebComponents/svgs/icons/PlayIcon';
import { useCreateEmailExecAssistantTemplateMutation } from '../../../../../queries/EmailExecAssistant/useCreateEmailExecAssistantTemplateMutation';
import { useUpdateEmailExecAssistantTemplateMutation } from '../../../../../queries/EmailExecAssistant/useUpdateEmailExecAssistantTemplateMutation';
import { Input } from '../../../../components-v2/Input';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';

/** The agent's registered assistant name (backend route segment + template resolver key). */
const EMAIL_EXEC_ASSISTANT_NAME = 'EmailExecAssistantAgent';

/**
 * Static "run my brief now" request. Sent to the agent's stream endpoint; the dates are omitted so the
 * agent uses its incremental "since last run" window. `_type: 'AIActionRequest'` is REQUIRED (the BE
 * otherwise parses it as a chat message); the action name is cast because it is not in the
 * `ApiModelType` union.
 */
const BRIEF_ACTION_REQUEST: Api.AIActionRequest = {
	_type: 'AIActionRequest',
	action: { _type: 'EmailExecBriefAction' as Api.ApiModelType } as Api.EmailExecBriefAction,
};

/** The user-editable subset of the template; everything else is server-managed and round-tripped. */
interface EditableFields {
	name: string;
	objectivePrompt: string;
	monitoredKeywords: string[];
	ignoreNoiseNotes: string;
}

const sameKeywords = (a: string[], b: string[]): boolean =>
	a.length === b.length && a.every((value, index) => value === b[index]);

const templateToEditableFields = (template: Api.EmailExecAssistantTemplate): EditableFields => {
	return {
		name: template.name ?? '',
		objectivePrompt: template.objectivePrompt ?? '',
		monitoredKeywords: template.monitoredKeywords ?? [],
		ignoreNoiseNotes: template.ignoreNoiseNotes ?? '',
	};
};

const initialEditableFields = (name: string): EditableFields => ({
	name: name ? `Exec Assistant for ${name}` : '',
	objectivePrompt: '',
	monitoredKeywords: [],
	ignoreNoiseNotes: '',
});

/**
 * The UI enforces one template per user; the backend list may (anomalously, via impersonation/API)
 * hold more than one. Bind deterministically to the most-recently-modified and ignore the rest.
 */
const selectBoundTemplate = (
	templates: Api.EmailExecAssistantTemplate[]
): Api.EmailExecAssistantTemplate | undefined => {
	if (templates.length <= 1) {
		return templates[0];
	}
	return [...templates].sort((a, b) => (b.lastModifiedDate ?? '').localeCompare(a.lastModifiedDate ?? ''))[0];
};

export const EmailExecAssistantSettingsContent: React.FC = () => {
	const id = React.useId();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('EmailExecAssistantSettings');

	const { data: templates } = useEmailExecAssistantTemplatesSuspenseQuery({
		meta: { toastErrorMessage: 'Failed to load your Email Exec Assistant settings.' },
		staleTime: Infinity,
	});
	// The full server template we round-trip on save (PUT is a whole-object replace); null until created.
	const loadedTemplate = selectBoundTemplate(templates) ?? null;
	// `baseline` is the last-saved editable state — the reference for dirty-tracking and Discard.
	const baseline = templates?.length
		? templateToEditableFields(loadedTemplate)
		: initialEditableFields(getDisplayName(userSession.user));

	const [form, setForm] = React.useState<EditableFields>(
		templates?.length
			? templateToEditableFields(loadedTemplate)
			: initialEditableFields(getDisplayName(userSession.user))
	);
	const [keywordInput, setKeywordInput] = React.useState('');

	const updateEmailExecAssistantMutation = useUpdateEmailExecAssistantTemplateMutation({
		onSuccess: template => {
			const saved = templateToEditableFields(template);
			logEvent('EmailExecTemplateUpdated', {
				isCreate: !loadedTemplate?.id,
				keywordCount: saved.monitoredKeywords.length,
				hasNoiseNotes: saved.ignoreNoiseNotes.length > 0,
			});
			updateEmailExecAssistantTemplatesRQCache(template);
			setForm(saved);
			toast.success('Email Exec Assistant settings saved.');
		},
		onError: error => {
			logApiError('EmailExecTemplateUpdate-Error', error);
			toast.error('Problem occurred saving your settings.');
		},
	});
	const createEmailExecAssistantMutation = useCreateEmailExecAssistantTemplateMutation({
		onSuccess: template => {
			const saved = templateToEditableFields(template);
			logEvent('EmailExecTemplateCreated', {
				isCreate: !loadedTemplate?.id,
				keywordCount: saved.monitoredKeywords.length,
				hasNoiseNotes: saved.ignoreNoiseNotes.length > 0,
			});
			updateEmailExecAssistantTemplatesRQCache(template);
			setForm(saved);
			toast.success('Email Exec Assistant settings saved.');
		},
		onError: error => {
			logApiError('EmailExecTemplateCreate-Error', error);
			toast.error('Problem occurred saving your settings.');
		},
	});

	// Run-now: create a fresh conversation pinned to the saved template, then stream the brief action.
	// We hold the stream open (progress state) only to detect completion/errors — the brief is emailed.
	const sawStreamErrorRef = React.useRef(false);
	const createConversation = useAIAssistantCreateConversationMutation({
		onError: error => {
			logEvent('EmailExecRunNow-Error');
			errorMessages.pushApiError(error);
		},
	});
	const briefStream = useStreamingResponseMutation<{ conversationId: string }>({
		mutationFn: ({ conversationId }) =>
			new Promise<Response>((resolve, reject) =>
				userSession.webServiceHelper.callWebServiceRawResponse(
					`ai/assistant/${EMAIL_EXEC_ASSISTANT_NAME}/conversation/${conversationId}/stream`,
					'POST',
					BRIEF_ACTION_REQUEST,
					resolve,
					reject
				)
			),
		onEvent: event => {
			// The backend reports brief failures as an SSE `Error` event while the stream still ends
			// normally, so flag it here to avoid a false "it's on its way" on completion.
			if (event.event === 'Error') {
				sawStreamErrorRef.current = true;
			}
		},
		onSuccess: () => {
			if (sawStreamErrorRef.current) {
				logEvent('EmailExecRunNow-Error');
				toast.error("We couldn't finish your brief. Please try again.");
				return;
			}
			toast.success('Your brief is on its way to your inbox.');
		},
		onError: () => {
			logEvent('EmailExecRunNow-Error');
			toast.error("We couldn't run your brief. Please try again.");
		},
	});

	const isGenerating = createConversation.isPending || briefStream.isLoading || briefStream.isStreaming;

	const isDirty =
		form.name !== baseline.name ||
		form.objectivePrompt !== baseline.objectivePrompt ||
		form.ignoreNoiseNotes !== baseline.ignoreNoiseNotes ||
		!sameKeywords(form.monitoredKeywords, baseline.monitoredKeywords);
	const isSaving = createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending;
	const canSave =
		isDirty && form.objectivePrompt.trim().length > 0 && form.name.trim().length > 0 && !isSaving && !isGenerating;
	const canRunNow = !!loadedTemplate?.id && !isDirty && !isGenerating && !isSaving;

	const updateField = <TKey extends keyof EditableFields>(key: TKey, value: EditableFields[TKey]) => {
		setForm(prev => ({ ...prev, [key]: value }));
	};

	const onKeywordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Enter' && event.key !== ',') {
			return;
		}
		// Straight-add: commit the typed text as chip(s) on Enter/comma — no dropdown item, no dialog.
		event.preventDefault();
		const additions = splitKeywordInput(keywordInput);
		if (additions.length === 0) {
			return;
		}
		setForm(prev => ({ ...prev, monitoredKeywords: addKeywords(prev.monitoredKeywords, additions) }));
		setKeywordInput('');
	};

	const onSave = () => {
		if (!canSave) {
			return;
		}
		if (loadedTemplate?.id) {
			updateEmailExecAssistantMutation.mutate({
				...loadedTemplate,
				name: form.name.trim(),
				objectivePrompt: form.objectivePrompt.trim(),
				monitoredKeywords: form.monitoredKeywords,
				ignoreNoiseNotes: form.ignoreNoiseNotes.trim() || undefined,
			});
		} else {
			createEmailExecAssistantMutation.mutate({
				name: form.name.trim(),
				objectivePrompt: form.objectivePrompt.trim(),
				monitoredKeywords: form.monitoredKeywords,
				ignoreNoiseNotes: form.ignoreNoiseNotes.trim() || undefined,
				isActive: true,
			});
		}
	};

	const onDiscard = () => {
		setForm(baseline);
		setKeywordInput('');
	};

	const onRunNow = () => {
		if (!canRunNow || !loadedTemplate?.id) {
			return;
		}
		logEvent('EmailExecRunNowClicked');
		sawStreamErrorRef.current = false;
		createConversation.mutate(
			{ assistantName: EMAIL_EXEC_ASSISTANT_NAME, agentTemplateId: loadedTemplate.id },
			{ onSuccess: conversation => briefStream.mutate({ conversationId: conversation.id }) }
		);
	};

	return (
		<div className={css(styleSheet.root)}>
			<div className={css(styleSheet.runNowContainer)}>
				<Button onClick={onRunNow} isLoading={isGenerating} disabled={!canRunNow}>
					<PlayIcon /> Run now
				</Button>
				{!loadedTemplate?.id ? (
					<span className={css(styleSheet.helpText)}>Save your settings to enable Run now.</span>
				) : isDirty ? (
					<span className={css(styleSheet.helpText)}>Save your changes to run a brief with them.</span>
				) : null}
			</div>
			<div className={css(styleSheet.field)}>
				<label className={css(styleSheet.label)} htmlFor={`${id}-name`}>
					Assistant name <span className={css(styleSheet.requiredIndicator)}>*</span>
				</label>
				<Input
					id={`${id}-name`}
					value={form.name}
					onChange={event => updateField('name', event.target.value)}
					required
				/>
			</div>
			<div className={css(styleSheet.field)}>
				<label className={css(styleSheet.label)} htmlFor='email-exec-objective'>
					What should your assistant prioritize? <span className={css(styleSheet.requiredIndicator)}>*</span>
				</label>
				<InputGroupV2>
					<InputGroupTextarea
						id='email-exec-objective'
						placeholder='Describe what matters in your inbox — the clients, topics, or situations you want watched and flagged. e.g. “Surface anything about tax-loss harvesting, Roth conversions, or money movement for my top clients.”'
						value={form.objectivePrompt}
						onChange={event => updateField('objectivePrompt', event.target.value)}
						required
						styles={[styleSheet.textArea]}
					/>
					<InputGroupAddon align='blockEnd'>
						<InputGroupText styles={[styleSheet.helpText]}>
							{form.objectivePrompt.trim().length > 0
								? 'Describe the clients, topics, or situations you want watched and flagged.'
								: 'Tell the assistant what to prioritize before saving.'}
						</InputGroupText>
					</InputGroupAddon>
				</InputGroupV2>
			</div>

			<div className={css(styleSheet.field)}>
				<span className={css(styleSheet.label)}>Always-flag keywords</span>
				<Combobox
					multiple
					value={form.monitoredKeywords}
					onValueChange={value => updateField('monitoredKeywords', value as string[])}
					inputValue={keywordInput}
					onInputValueChange={setKeywordInput}
				>
					<ComboboxChips>
						<ComboboxValue>
							{(keywords: string[]) => (
								<>
									{keywords.map(keyword => (
										<ComboboxChip key={keyword} aria-label={keyword}>
											{keyword}
											<ComboboxChipRemove />
										</ComboboxChip>
									))}
									<ComboboxChipsInput
										placeholder={keywords.length > 0 ? '' : 'Type a keyword and press Enter'}
										onKeyDown={onKeywordKeyDown}
									/>
								</>
							)}
						</ComboboxValue>
					</ComboboxChips>
				</Combobox>
				<span className={css(styleSheet.helpText)}>
					Emails containing any of these are always surfaced as action-needed, even if nothing else stands out. <br />{' '}
				</span>
				<span className={css(styleSheet.helpText)}>
					{form.monitoredKeywords.length}/{MAX_KEYWORDS}
				</span>
			</div>

			<div className={css(styleSheet.field)}>
				<label className={css(styleSheet.label)} htmlFor='email-exec-noise'>
					What to ignore
				</label>
				<InputGroupV2>
					<InputGroupTextarea
						id='email-exec-noise'
						placeholder='Describe the noise you don’t want in your brief. e.g. “Ignore wholesaler pitches, fixed-income solicitations, and conference invitations.”'
						value={form.ignoreNoiseNotes}
						onChange={event =>
							updateField('ignoreNoiseNotes', event.target.value.slice(0, MAX_IGNORE_NOISE_NOTES_LENGTH))
						}
						styles={[styleSheet.textArea]}
					/>
					<InputGroupAddon align='blockEnd'>
						<InputGroupText styles={[styleSheet.helpText]}>
							{form.ignoreNoiseNotes.length}/{MAX_IGNORE_NOISE_NOTES_LENGTH}
						</InputGroupText>
					</InputGroupAddon>
				</InputGroupV2>
			</div>

			<div className={css(styleSheet.footer)}>
				<Button
					onClick={onSave}
					isLoading={createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending}
					disabled={!canSave}
				>
					Save
				</Button>
				{isDirty ? (
					<Button
						variant='secondary'
						onClick={onDiscard}
						disabled={createEmailExecAssistantMutation.isPending || updateEmailExecAssistantMutation.isPending}
					>
						Discard changes
					</Button>
				) : null}
			</div>
		</div>
	);
};

export const EmailExecAssistantSettings: React.FC = () => {
	const userSession = useUserSession();

	// MUST stay outside Suspense: useSuspenseQuery can't be disabled, so we gate by not rendering Content.
	if (!userSession.user?.emailProviderFeatures?.email?.canViewSentMessages) {
		return (
			<div className={css(styleSheet.root)}>
				<span className={css(styleSheet.statusLine)}>
					The Email Exec Assistant needs an inbox it can read. Your current email connection is send-only, so it can’t
					be used here. Connect an account with full mailbox access (Gmail, Microsoft 365, Exchange, Yahoo, AOL, or
					IMAP) to enable it.
				</span>
			</div>
		);
	}

	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<ErrorBoundary
					onReset={reset}
					fallbackRender={({ resetErrorBoundary }) => (
						<div className={css(styleSheet.root)}>
							<span className={css(styleSheet.statusLine)}>We couldn’t load your Email Exec Assistant settings.</span>
							<Button variant='outline' onClick={() => resetErrorBoundary()}>
								Try again
							</Button>
						</div>
					)}
				>
					<React.Suspense fallback={<LoadingSpinner type='small' />}>
						<EmailExecAssistantSettingsContent />
					</React.Suspense>
				</ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
};
